/**
 * campaigns.ts (MKT-REDESIGN-01 M1, 2026-09-03) — the campaign RECORD and the
 * hooks around it. A campaign is created, then SCHEDULED with a recipient
 * list; scheduling writes one pegasus_outbox row per recipient (consent
 * checked, duplicates dropped) and the comms-run job drains them at the
 * campaign's throttle. While comms are in testing the rows are HELD and the
 * campaign waits for an explicit Release at go-live — never automatic.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type CampaignStatus = "draft" | "scheduled" | "held" | "sending" | "sent" | "cancelled" | "failed";

export interface Campaign {
  id: string;
  name: string;
  channel: "email" | "sms";
  template_key: string | null;
  template_version: number | null;
  /** M6: a TEXT campaign carries its own message (tokens {{first_name}}, {{portal_url}}); the STOP line is added at schedule time. */
  sms_body: string | null;
  audience: Record<string, unknown>;
  audience_label: string | null;
  status: CampaignStatus;
  scheduled_for: string | null;
  started_at: string | null;
  finished_at: string | null;
  recipient_count: number;
  excluded_count: number;
  sent_count: number;
  failed_count: number;
  suppressed_count: number;
  throttle_per_run: number;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignRecipient {
  custno: string | null;
  locno?: string | null;
  name?: string | null;
  to: string;
}

export const CAMPAIGNS_KEY = ["campaigns"] as const;

export const STATUS_META: Record<CampaignStatus, { label: string; chip: string }> = {
  draft: { label: "Draft", chip: "bg-slate-100 text-slate-600" },
  scheduled: { label: "Scheduled", chip: "bg-sky-50 text-sky-700 ring-1 ring-sky-200" },
  held: { label: "Held — comms in testing", chip: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
  sending: { label: "Sending", chip: "bg-brand-50 text-brand-700 ring-1 ring-brand-200" },
  sent: { label: "Sent", chip: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
  cancelled: { label: "Cancelled", chip: "bg-slate-100 text-slate-500" },
  failed: { label: "Failed", chip: "bg-rose-50 text-rose-700 ring-1 ring-rose-200" },
};

export function useCampaigns() {
  return useQuery({
    queryKey: CAMPAIGNS_KEY,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    queryFn: async (): Promise<Campaign[]> => {
      const { data, error } = await supabase.from("pegasus_campaign").select("*").is("deleted_at", null)
        .order("created_at", { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as Campaign[];
    },
  });
}

export function useCampaignRecipients(campaignId: string | null) {
  return useQuery({
    queryKey: [...CAMPAIGNS_KEY, "recipients", campaignId],
    enabled: !!campaignId,
    refetchInterval: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.from("pegasus_outbox")
        .select("id, custno, customer_name, to_address, status, status_detail, sent_at, scheduled_for")
        .eq("campaign_id", campaignId!).order("status").order("customer_name").limit(2000);
      if (error) throw error;
      return (data ?? []) as { id: string; custno: string | null; customer_name: string | null; to_address: string; status: string; status_detail: string | null; sent_at: string | null; scheduled_for: string }[];
    },
  });
}

/** Create the record, then schedule it with the resolved recipient list. */
export function useLaunchCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      name: string; channel?: "email" | "sms"; templateKey: string | null; templateVersion?: number | null; smsBody?: string | null;
      audience: Record<string, unknown>; audienceLabel: string; recipients: CampaignRecipient[];
      scheduledFor?: string | null; notes?: string | null;
    }): Promise<Campaign> => {
      const { data: u } = await supabase.auth.getUser();
      const { data: created, error } = await supabase.from("pegasus_campaign").insert({
        name: v.name.trim(), channel: v.channel ?? "email", template_key: v.templateKey, template_version: v.templateVersion ?? null,
        sms_body: v.channel === "sms" ? (v.smsBody ?? null) : null,
        audience: v.audience, audience_label: v.audienceLabel, notes: v.notes ?? null, created_by: u.user?.email ?? null,
      }).select("id").single();
      if (error) throw error;
      const { data: scheduled, error: sErr } = await supabase.rpc("pegasus_campaign_schedule", {
        p_campaign_id: created.id,
        p_recipients: v.recipients,
        p_scheduled_for: v.scheduledFor ?? new Date().toISOString(),
      });
      if (sErr) throw sErr;
      return scheduled as Campaign;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

export function useCancelCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("pegasus_campaign_cancel", { p_campaign_id: id });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

export function useReleaseCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("pegasus_campaign_release", { p_campaign_id: id });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

/** Rough wall-clock estimate for a throttled send (comms-run every 10 minutes). */
export function estimateDuration(recipients: number, perRun = 100): string {
  const runs = Math.ceil(recipients / Math.max(1, perRun));
  const minutes = runs * 10;
  if (minutes <= 10) return "within about 10 minutes";
  if (minutes < 120) return `about ${minutes} minutes`;
  if (minutes < 60 * 36) return `about ${Math.round(minutes / 60)} hours`;
  return `about ${Math.round(minutes / 60 / 24)} days`;
}

/** MKT M4: what happened after the send — opens, clicks, unsubscribes, and the
 *  bookings / invoices within 30 days attributed to this campaign (most recent
 *  campaign wins). Computed live by pegasus_campaign_results. */
export interface CampaignResults {
  sent: number; opened: number; opens: number; clicked: number; clicks: number;
  top_links: { url: string; n: number }[]; bounced: number; unsubscribed: number;
  booked_30d: number; bookings_30d: number; paid_30d: number; revenue_30d: number;
  first_sent_at: string | null; last_sent_at: string | null; window_open: boolean | null;
}
export function useCampaignResults(campaignId: string | null) {
  return useQuery({
    queryKey: [...CAMPAIGNS_KEY, "results", campaignId],
    enabled: !!campaignId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CampaignResults> => {
      const { data, error } = await supabase.rpc("pegasus_campaign_results", { p_campaign_id: campaignId! });
      if (error) throw error;
      return data as CampaignResults;
    },
  });
}
